import type {
  Agent,
  Artifact,
  CostControl,
  Flow,
  FlowEdge,
  FlowNode,
  Guardrails,
  HumanOversightMode,
  NodeKind,
  Run,
  RunEvent,
  ToolId,
} from '../types';
import { defaultCostControl, defaultGuardrails, newAgent, newFlow } from '../lib/factories';
import { nowIso } from '../lib/utils';

type AnyRecord = Record<string, unknown>;

const AGENT_STATUSES = new Set(['active', 'draft', 'archived']);
const FLOW_STATUSES = new Set(['draft', 'published', 'inactive']);
const OVERSIGHT_MODES = new Set(['in_the_loop', 'on_the_loop', 'out_of_the_loop']);
const RUN_STATUSES = new Set(['running', 'success', 'error', 'paused_for_human']);
const NODE_KINDS = new Set([
  'trigger',
  'agent',
  'llm',
  'tool',
  'router',
  'loop',
  'memory',
  'kb',
  'human_review',
  'output',
]);

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
}

function asRecord(value: unknown): AnyRecord {
  const parsed = parseMaybeJson(value);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as AnyRecord) : {};
}

function entityRecord(value: unknown): AnyRecord {
  const row = asRecord(value);
  const data = asRecord(row.data);
  return Object.keys(data).length > 0 ? { ...row, ...data } : row;
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function requiredText(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

function numberValue(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function uniqueById<T extends { id: string }>(items: T[], prefix: string): T[] {
  const seen = new Map<string, number>();
  return items.map((item, index) => {
    const baseId = requiredText(item.id, `${prefix}_legacy_${index}`);
    const count = seen.get(baseId) ?? 0;
    seen.set(baseId, count + 1);
    return count === 0 ? { ...item, id: baseId } : { ...item, id: `${baseId}_${count}` };
  });
}

function normalizeCostControl(value: unknown): CostControl {
  const defaults = defaultCostControl();
  const raw = asRecord(value);
  const onExceed = text(raw.onExceed);
  return {
    areaId: text(raw.areaId) || undefined,
    personId: text(raw.personId) || undefined,
    monthlyCapBRL: numberValue(raw.monthlyCapBRL, defaults.monthlyCapBRL),
    perRunCapBRL: numberValue(raw.perRunCapBRL, defaults.perRunCapBRL),
    spentBRL: numberValue(raw.spentBRL, defaults.spentBRL),
    onExceed: onExceed === 'block' || onExceed === 'require_approval' ? onExceed : defaults.onExceed,
  };
}

function normalizeGuardrails(value: unknown): Guardrails {
  const defaults = defaultGuardrails();
  const raw = asRecord(value);
  const contentFilters = asRecord(raw.contentFilters);
  const limits = asRecord(raw.limits);
  const onViolation = text(raw.onViolation);
  return {
    ...defaults,
    inputSchema: text(raw.inputSchema) || undefined,
    outputFormat: raw.outputFormat === 'json' ? 'json' : defaults.outputFormat,
    outputSchema: text(raw.outputSchema) || undefined,
    contentFilters: {
      pii: booleanValue(contentFilters.pii, defaults.contentFilters.pii),
      toxicity: booleanValue(contentFilters.toxicity, defaults.contentFilters.toxicity),
      jailbreak: booleanValue(contentFilters.jailbreak, defaults.contentFilters.jailbreak),
      blockedTopics: stringArray(contentFilters.blockedTopics),
    },
    redactPII: booleanValue(raw.redactPII, defaults.redactPII),
    moderationMode: raw.moderationMode === 'model' ? 'model' : defaults.moderationMode,
    moderationModel: text(raw.moderationModel, defaults.moderationModel),
    allowedTools: stringArray(raw.allowedTools) as ToolId[],
    limits: {
      maxTokens: numberValue(limits.maxTokens, defaults.limits.maxTokens),
      maxToolCalls: numberValue(limits.maxToolCalls, defaults.limits.maxToolCalls),
      maxLoopIterations: numberValue(limits.maxLoopIterations, defaults.limits.maxLoopIterations),
      timeoutSec: numberValue(limits.timeoutSec, defaults.limits.timeoutSec),
      maxCostBRL: numberValue(limits.maxCostBRL, defaults.limits.maxCostBRL),
    },
    onViolation: onViolation === 'block' || onViolation === 'require_human' ? onViolation : defaults.onViolation,
    fallbackMessage: text(raw.fallbackMessage, defaults.fallbackMessage),
    humanApprovalActions: stringArray(raw.humanApprovalActions),
  };
}

export function normalizeAgent(value: unknown, index = 0): Agent {
  const raw = entityRecord(value);
  const base = newAgent();
  const createdAt = text(raw.createdAt, base.createdAt);
  return {
    ...base,
    ...(raw as Partial<Agent>),
    id: requiredText(raw.id, `agent_legacy_${index}`),
    name: requiredText(raw.name, requiredText(raw.title, 'Agente sem nome')),
    description: text(raw.description),
    systemPrompt: text(raw.systemPrompt, base.systemPrompt),
    model: text(raw.model, base.model),
    temperature: numberValue(raw.temperature, base.temperature),
    maxTokens: numberValue(raw.maxTokens, base.maxTokens),
    tools: stringArray(raw.tools) as ToolId[],
    memory: raw.memory === 'none' || raw.memory === 'long' ? raw.memory : base.memory,
    knowledgeBaseId: text(raw.knowledgeBaseId) || undefined,
    knowledgeBaseIds: stringArray(raw.knowledgeBaseIds),
    embeddingModel: text(raw.embeddingModel, base.embeddingModel),
    files: Array.isArray(raw.files) ? (raw.files as Agent['files']) : [],
    artifacts: Array.isArray(raw.artifacts) ? (raw.artifacts as Artifact[]) : [],
    guardrails: normalizeGuardrails(raw.guardrails),
    costControl: normalizeCostControl(raw.costControl),
    status: AGENT_STATUSES.has(text(raw.status)) ? (raw.status as Agent['status']) : base.status,
    createdAt,
    updatedAt: text(raw.updatedAt, createdAt),
  };
}

export function normalizeAgentList(value: unknown): Agent[] {
  return uniqueById((Array.isArray(value) ? value : []).map((item, index) => normalizeAgent(item, index)), 'agent');
}

function normalizeNode(value: unknown, index: number): FlowNode {
  const raw = entityRecord(value);
  const position = asRecord(raw.position);
  const kind = NODE_KINDS.has(text(raw.kind)) ? (raw.kind as NodeKind) : 'trigger';
  return {
    ...(raw as Partial<FlowNode>),
    id: requiredText(raw.id, `node_legacy_${index}`),
    kind,
    position: {
      x: numberValue(position.x, 120 + index * 80),
      y: numberValue(position.y, 200),
    },
    data: asRecord(raw.data),
    parentId: text(raw.parentId) || undefined,
    oversightOverride: OVERSIGHT_MODES.has(text(raw.oversightOverride))
      ? (raw.oversightOverride as HumanOversightMode)
      : undefined,
  };
}

function normalizeEdge(value: unknown, index: number): FlowEdge | null {
  const raw = entityRecord(value);
  const source = requiredText(raw.source, '');
  const target = requiredText(raw.target, '');
  if (!source || !target) return null;
  return {
    ...(raw as Partial<FlowEdge>),
    id: requiredText(raw.id, `edge_legacy_${index}`),
    source,
    target,
    sourceHandle: text(raw.sourceHandle) || undefined,
    targetHandle: text(raw.targetHandle) || undefined,
    isLoopBack: booleanValue(raw.isLoopBack, false),
    label: text(raw.label) || undefined,
  };
}

export function normalizeFlow(value: unknown, index = 0): Flow {
  const raw = entityRecord(value);
  const base = newFlow();
  const createdAt = text(raw.createdAt, base.createdAt);
  const nodes = Array.isArray(raw.nodes) ? raw.nodes.map(normalizeNode) : base.nodes;
  const edges = Array.isArray(raw.edges)
    ? raw.edges.map(normalizeEdge).filter((edge): edge is FlowEdge => Boolean(edge))
    : [];

  return {
    ...base,
    ...(raw as Partial<Flow>),
    id: requiredText(raw.id, `flow_legacy_${index}`),
    name: requiredText(raw.name, requiredText(raw.title, 'Fluxo sem nome')),
    description: text(raw.description),
    status: FLOW_STATUSES.has(text(raw.status)) ? (raw.status as Flow['status']) : base.status,
    oversightMode: OVERSIGHT_MODES.has(text(raw.oversightMode)) ? (raw.oversightMode as HumanOversightMode) : base.oversightMode,
    agentId: text(raw.agentId) || undefined,
    costControl: normalizeCostControl(raw.costControl),
    favorite: booleanValue(raw.favorite, base.favorite ?? false),
    nodes,
    edges,
    createdAt,
    updatedAt: text(raw.updatedAt, createdAt),
  };
}

export function normalizeFlowList(value: unknown): Flow[] {
  return uniqueById((Array.isArray(value) ? value : []).map((item, index) => normalizeFlow(item, index)), 'flow');
}

function normalizeRunEvent(value: unknown, index: number): RunEvent {
  const raw = entityRecord(value);
  const status = text(raw.status);
  return {
    ...(raw as Partial<RunEvent>),
    nodeId: requiredText(raw.nodeId, `node_legacy_${index}`),
    iteration: raw.iteration == null ? undefined : numberValue(raw.iteration, 0),
    status: status === 'pending' || status === 'running' || status === 'done' || status === 'awaiting_human' ? status : 'error',
    message: text(raw.message),
    timestamp: text(raw.timestamp, nowIso()),
    output: text(raw.output) || undefined,
  };
}

export function normalizeRun(value: unknown, index = 0): Run {
  const raw = entityRecord(value);
  const startedAt = text(raw.startedAt, nowIso());
  const status = text(raw.status);
  return {
    ...(raw as Partial<Run>),
    id: requiredText(raw.id, `run_legacy_${index}`),
    flowId: text(raw.flowId),
    flowName: text(raw.flowName),
    status: RUN_STATUSES.has(status) ? (status as Run['status']) : 'success',
    startedAt,
    finishedAt: text(raw.finishedAt) || undefined,
    durationMs: raw.durationMs == null ? undefined : numberValue(raw.durationMs, 0),
    costBRL: raw.costBRL == null ? undefined : numberValue(raw.costBRL, 0),
    events: Array.isArray(raw.events) ? raw.events.map(normalizeRunEvent) : [],
    artifacts: Array.isArray(raw.artifacts) ? (raw.artifacts as Artifact[]) : [],
    input: text(raw.input) || undefined,
    finalOutput: text(raw.finalOutput) || undefined,
  };
}

export function normalizeRunList(value: unknown): Run[] {
  return uniqueById((Array.isArray(value) ? value : []).map((item, index) => normalizeRun(item, index)), 'run');
}
